import { useState } from "react";
import Papa from "papaparse";
import { decode as iconvDecode } from "iconv-lite";
import "iconv-lite/encodings";

type EncodingOption = "auto" | "utf-8" | "cp949";

function hasManyReplacementChars(s: string, thresholdRatio = 0.01) {
  // U+FFFD (�) 가 너무 많으면 디코딩 실패로 간주
  if (!s) return false;
  const bad = (s.match(/\uFFFD/g) || []).length;
  return bad / s.length > thresholdRatio;
}

function detectBOM(buf: Uint8Array): "utf-8" | null {
  // UTF-8 BOM: EF BB BF
  if (buf.length >= 3 && buf[0] === 0xef && buf[1] === 0xbb && buf[2] === 0xbf)
    return "utf-8";
  return null;
}

async function decodeCsv(
  file: File,
  encoding: EncodingOption
): Promise<{ csv: string; usedEncoding: "utf-8" | "cp949" }> {
  const ab = await file.arrayBuffer();
  const bytes = new Uint8Array(ab);

  // 1) 명시 인코딩이면 그대로
  if (encoding === "utf-8") {
    const txt = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { csv: txt, usedEncoding: "utf-8" };
  }
  if (encoding === "cp949") {
    const buf = Buffer.from(bytes); // Ensure the input is a Node.js Buffer
    const txt = iconvDecode(buf, "cp949"); // iconv-lite가 브라우저에서 CP949 지원
    return { csv: txt, usedEncoding: "cp949" };
  }

  // 2) auto 모드: BOM → UTF-8 우선 → 실패 시 CP949
  const bom = detectBOM(bytes);
  if (bom === "utf-8") {
    const txt = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { csv: txt, usedEncoding: "utf-8" };
  }

  const utf8 = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  if (!hasManyReplacementChars(utf8)) {
    return { csv: utf8, usedEncoding: "utf-8" };
  }

  // UTF-8로 깨지면 CP949 재시도
  // iconv-lite의 decode 함수는 Node.js에서 Buffer 인자를 기대하나,
  // 브라우저 환경에서는 Uint8Array도 지원. 유형 오류는 무시 가능.
  // 타입 에러 해결을 위해 Buffer 변환 (Node.js 환경에서만 필요)
  // 브라우저만 쓸 경우, 아래 라인만 사용해도 문제 없음:
  // const cp949 = iconvDecode(bytes, "cp949");
  let cp949: string;
  try {
    // @ts-ignore
    cp949 = iconvDecode(bytes, "cp949");
  } catch {
    // (브라우저 환경인데 타입 오류만 나는 경우 방지)
    const buf = Buffer.from(bytes);
    cp949 = iconvDecode(buf, "cp949");
  }
  return { csv: cp949, usedEncoding: "cp949" };
}

export function useCsvPreview() {
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [usedEncoding, setUsedEncoding] = useState<"utf-8" | "cp949" | null>(
    null
  );

  /**
   * 파일에서 startRow부터 5행만 미리보기 파싱
   * @param file CSV 파일
   * @param startRow 1부터 시작(헤더 없이 N번째 행부터)
   * @param encoding 'auto' | 'utf-8' | 'cp949'
   */
  const parseFile = async (
    file: File | null,
    startRow: number = 1,
    encoding: EncodingOption = "auto"
  ) => {
    if (!file) return;

    const { csv, usedEncoding } = await decodeCsv(file, encoding);
    setUsedEncoding(usedEncoding);

    const result: string[][] = [];
    const start = Math.max(0, startRow - 1);
    const end = start + 5;
    let currentRow = 0;

    Papa.parse<string[]>(csv, {
      header: false,
      skipEmptyLines: true,
      step: ({ data }, parser) => {
        if (currentRow >= start && currentRow <= end) {
          result.push(data);
        }
        if (currentRow > end) {
          parser.abort();
        }
        currentRow++;
      },
      complete: () => setPreviewData(result),
    });
  };

  const resetPreview = () => {
    setPreviewData([]);
    setUsedEncoding(null);
  };

  return { previewData, usedEncoding, parseFile, resetPreview };
}
